import moment from 'moment';
import { useEffect, useState, useCallback, useRef } from 'react';
import useHttpApiCall from '../../../services/useHttpApiCall';
import SetDownloadComponent from '../components/set.download.component';
import { useDispatch, useSelector } from 'react-redux';
import { setExcelDownload } from '../../../redux/exceldownload/exceldownload.action';
import { replaceUnderscoreWithHyphen } from '../../../utilities/common.computations';

export default function SetDownloadContainer({
  checkedItems,
  openDownloadScreen,
  setShowToast,
  apiEquipmentList,
  selectSensorClassification,
  sensorUnit
}) {
  const INITIAL_DATE = new Date().setHours(0, 0, 0, 0);
  const dispatch = useDispatch();
  const [payload, setPayload] = useState(null);
  const [data, error, loading] = useHttpApiCall(payload);
  const set_excel_download = useSelector((state) => state.excelDownload.set_excel_download);
  const [currentSingleDataTypeSensorValueInfo, setCurrentSingleDataTypeSensorValueInfo] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Refs to track previous values and prevent infinite loops
  const previousDataRef = useRef();
  const previousErrorRef = useRef();
  const isProcessingRef = useRef(false);

  const sensorClassificationSlug = {
    energy: {
      name: 'Energy',
      unit: 'kW',
      values: ['Daily', 'Hourly', '2-min'],
      timeIntervals: 2,
      maxSelectionDays: 30
    },
    temperature: {
      name: 'Temperature',
      unit: '°C',
      values: [
        {
          name: '30-min',
          value: 'temperature-humidity-least-interval',
          timeIntervals: 30,
          maxSelectionDays: 90
        }
      ]
    },
    humidity: {
      name: 'Humidity',
      unit: '%',
      values: [
        {
          name: '30-min',
          value: 'temperature-humidity-least-interval',
          timeIntervals: 30,
          maxSelectionDays: 90
        }
      ]
    },
    total_flow: {
      name: 'Total flow',
      unit: 'm3',
      values: [
        {
          name: 'Daily',
          value: 'daily',
          timeIntervals: 30,
          maxSelectionDays: false,
          hideTime: true
        },
        { name: 'Hourly', value: 'hourly', timeIntervals: 60, maxSelectionDays: false }
      ]
    },
    flow_rate: {
      name: 'Flow rate',
      unit: 'm3/h',
      values: [{ name: '2-min', value: 'least-interval', timeIntervals: 2, maxSelectionDays: 30 }]
    }
  };

  const initialFormObject = {
    data_type: {
      value: {
        kW: true,
        kWh: false
      }
    },
    values: {
      value: {
        daily: false,
        hourly: false,
        least_interval: true
      }
    },
    start_date: {
      value: INITIAL_DATE
    },
    end_date: {
      value: INITIAL_DATE
    }
  };

  const [formData, setFormData] = useState(initialFormObject);

  // Safe localStorage functions
  const getDownloadIds = useCallback(() => {
    try {
      const stored = localStorage.getItem('download_ids');
      return stored ? stored.split(',').filter(id => id && id.trim() !== '') : [];
    } catch (error) {
      console.error('Failed to access localStorage:', error);
      return [];
    }
  }, []);

  const saveDownloadId = useCallback((id) => {
    try {
      const downloadIds = getDownloadIds();
      downloadIds.push(id);
      localStorage.setItem('download_ids', downloadIds.join(','));
      return true;
    } catch (error) {
      console.error('Failed to save to localStorage:', error);
      return false;
    }
  }, [getDownloadIds]);

  useEffect(() => {
    if (sensorUnit && sensorClassificationSlug[sensorUnit]) {
      setCurrentSingleDataTypeSensorValueInfo(sensorClassificationSlug[sensorUnit].values[0]);
    }
  }, [sensorUnit]);

  // IMPROVED: Better equipment list extraction
  const getEquipmentList = useCallback(() => {
    if (!apiEquipmentList || !Array.isArray(apiEquipmentList)) {
      console.error('Invalid equipment list:', apiEquipmentList);
      return [];
    }

    if (!checkedItems || typeof checkedItems !== 'object') {
      console.error('Invalid checked items:', checkedItems);
      return [];
    }
    
    let equipmentArray = [];
    let selectedCount = 0;

    // Get all checked item keys
    const checkedKeys = Object.keys(checkedItems).filter(key => checkedItems[key]);
    
    //console.log('Checked keys:', checkedKeys);
   // console.log('Available equipment list:', apiEquipmentList);

    checkedKeys.forEach((itemKey) => {
      // Try multiple strategies to find the correct equipment
      let equipment = null;

      // Strategy 1: Find by exact ID match from the itemKey
      const keyParts = itemKey.split('_');
      const potentialId = keyParts[keyParts.length - 1]; // Get last part as ID
      
      equipment = apiEquipmentList.find(eq => 
        eq.id && eq.id.toString() === potentialId
      );

      // Strategy 2: Find by name or other identifier
      if (!equipment) {
        equipment = apiEquipmentList.find(eq => 
          eq.name === itemKey || eq.identifier === itemKey
        );
      }

      // Strategy 3: If itemKey is an index, use it directly
      if (!equipment && !isNaN(itemKey)) {
        equipment = apiEquipmentList[parseInt(itemKey)];
      }

      if (equipment && equipment.id) {
        equipmentArray.push({
          source_mapping: equipment.id,
          name: equipment.name || 'Unknown Equipment'
        });
        selectedCount++;
        console.log(`Added equipment: ${equipment.name} (ID: ${equipment.id})`);
      } else {
        console.warn('Could not find equipment for key:', itemKey, 'Available:', apiEquipmentList);
      }
    });

    console.log(`Successfully added ${selectedCount} equipment items to download request`);
    console.log('Final equipment array for API:', equipmentArray);
    
    return equipmentArray;
  }, [checkedItems, apiEquipmentList]);

  const validateForm = useCallback((equipmentList, startDate, endDate) => {
    const errors = [];

    if (equipmentList.length === 0) {
      errors.push('Please select at least one equipment');
    }

    if (!startDate || !endDate) {
      errors.push('Please select both start and end dates');
    } else if (startDate > endDate) {
      errors.push('Start date cannot be after end date');
    }

    const daysDifference = moment(endDate).diff(moment(startDate), 'days');
    if (daysDifference > 365) {
      errors.push('Date range cannot exceed 365 days');
    }

    return errors;
  }, []);

  const onFormSubmit = async (e) => {
    e.preventDefault();
    
    if (isSubmitting) {
      console.log('Already submitting, please wait...');
      return;
    }

    setIsSubmitting(true);

    try {
      const postEquipmentsSendArray = getEquipmentList();
      const startDate = formData.start_date.value;
      const endDate = formData.end_date.value;

      const validationErrors = validateForm(postEquipmentsSendArray, startDate, endDate);
      if (validationErrors.length > 0) {
        setShowToast({
          type: 'error',
          message: validationErrors[0],
          show: true
        });
        setIsSubmitting(false);
        return;
      }

      let postDataType, postDataValues;

      if (selectSensorClassification && sensorUnit && sensorUnit !== 'energy') {
        postDataType = sensorUnit;
        postDataValues = currentSingleDataTypeSensorValueInfo?.value || 
                        sensorClassificationSlug[sensorUnit]?.values[0]?.value;
      } else {
        postDataType = Object.keys(formData.data_type.value).find(
          (k) => formData.data_type.value[k]
        );
        postDataValues = Object.keys(formData.values.value).find(
          (k) => formData.values.value[k]
        );
      }

      if (!postDataType || !postDataValues) {
        throw new Error('Invalid data type or values selection');
      }

      const postStartDate = moment(startDate).format('YYYY-MM-DD HH:mm:ss');
      const postEndDate = moment(endDate).format('YYYY-MM-DD HH:mm:ss');

      // Log detailed information about the download request
      console.log('=== DOWNLOAD REQUEST DETAILS ===');
      console.log('Selected equipment count:', postEquipmentsSendArray.length);
      console.log('Equipment IDs:', postEquipmentsSendArray.map(eq => eq.source_mapping));
      console.log('Data type:', postDataType);
      console.log('Data resolution:', postDataValues);
      console.log('Date range:', `${postStartDate} to ${postEndDate}`);
      console.log('All data in one Excel sheet: YES');
      console.log('================================');

      // Reset processing flag before setting payload
      isProcessingRef.current = false;
      setPayload({
        auth: true,
        url: 'v0/downloads/',
        method: 'POST',
        data: {
          level: 'equipment',
          data: replaceUnderscoreWithHyphen(postDataType),
          data_resolution: replaceUnderscoreWithHyphen(postDataValues),
          start_date_time: postStartDate,
          end_date_time: postEndDate,
          equipments: postEquipmentsSendArray
          // Note: All equipment IDs are sent in one array, 
          // which should generate one combined Excel file
        }
      });

    } catch (error) {
      console.error('Error preparing download request:', error);
      setShowToast({
        type: 'error',
        message: 'Failed to prepare download request',
        show: true
      });
      setIsSubmitting(false);
    }
  };

  // Add a function to debug checked items
  const debugCheckedItems = useCallback(() => {
    console.log('=== DEBUG CHECKED ITEMS ===');
    console.log('CheckedItems object:', checkedItems);
    console.log('Selected keys:', Object.keys(checkedItems).filter(key => checkedItems[key]));
    console.log('Total selected:', Object.keys(checkedItems).filter(key => checkedItems[key]).length);
    console.log('Available equipment list:', apiEquipmentList);
    console.log('==========================');
  }, [checkedItems, apiEquipmentList]);

  // Call debug on mount to verify data
  useEffect(() => {
    debugCheckedItems();
  }, [debugCheckedItems]);

  const handleSingleDataTypeSensorValue = (e) => {
    if (!e?.target?.id) {
      console.error('Invalid event target for sensor value selection');
      return;
    }

    const dateInitialObject = {
      end_date: { value: INITIAL_DATE },
      start_date: { value: INITIAL_DATE }
    };

    try {
      const currentSingleDataTypeSensorValueObject = sensorClassificationSlug[sensorUnit]?.values?.find(
        (k) => k.value === e.target.id
      );

      if (currentSingleDataTypeSensorValueObject) {
        setFormData((prev) => ({ ...prev, ...dateInitialObject }));
        setCurrentSingleDataTypeSensorValueInfo(currentSingleDataTypeSensorValueObject);
      } else {
        console.warn('No sensor value found for ID:', e.target.id);
      }
    } catch (error) {
      console.error('Error handling sensor value selection:', error);
    }
  };

  const onSelectOption = (e) => {
    if (!e?.target?.id || !e?.target?.parentNode) {
      console.error('Invalid event target for option selection');
      return;
    }

    const category = e.target.parentNode.getAttribute('id');
    const option = e.target.id;

    if (!category || !formData[category]) {
      console.error('Invalid category or option:', category, option);
      return;
    }

    const selectedValueCopy = { ...formData[category].value };
    const dependentValueCopy = { ...formData.values.value };
    const dateInitialObject = {
      end_date: { value: INITIAL_DATE },
      start_date: { value: INITIAL_DATE }
    };

    if (!formData[category].value[option]) {
      dispatch(
        setExcelDownload({
          ...set_excel_download,
          download_status: false
        })
      );
    }

    changeOneKeyTrue(selectedValueCopy, option);

    try {
      if (category === 'data_type') {
        if (option === 'kW') {
          changeOneKeyTrue(dependentValueCopy, 'least_interval');
          setFormData((prev) => ({
            ...prev,
            [category]: { ...prev[category], value: selectedValueCopy },
            values: { ...prev.values, value: dependentValueCopy },
            ...dateInitialObject
          }));
        } else if (option === 'kWh') {
          changeOneKeyTrue(dependentValueCopy, 'daily');
          setFormData((prev) => ({
            ...prev,
            [category]: { ...prev[category], value: selectedValueCopy },
            values: { ...prev.values, value: dependentValueCopy }
          }));
        }
      } else if (category === 'values') {
        if (option === 'daily') {
          setFormData((prev) => ({
            ...prev,
            [category]: {
              ...prev[category],
              value: selectedValueCopy
            }
          }));
        } else {
          setFormData((prev) => ({
            ...prev,
            [category]: { ...prev[category], value: selectedValueCopy },
            ...dateInitialObject
          }));
        }
      }
    } catch (error) {
      console.error('Error updating form data:', error);
    }
  };

  const changeOneKeyTrue = (object, conditionValue) => {
    if (!object || typeof object !== 'object') {
      console.error('Invalid object provided to changeOneKeyTrue');
      return;
    }

    Object.keys(object).forEach((v) => {
      object[v] = v === conditionValue;
    });
  };

  // Handle API responses without infinite loops
  useEffect(() => {
    if (isProcessingRef.current) return;
    
    const currentData = data;
    const currentError = error;

    const dataChanged = previousDataRef.current !== currentData;
    const errorChanged = previousErrorRef.current !== currentError;

    if (!dataChanged && !errorChanged) return;

    if (currentError && errorChanged) {
      console.error('Download API error:', currentError);
      setShowToast({
        type: 'error',
        message: 'Download request failed. Please try again.',
        show: true
      });
      setIsSubmitting(false);
      previousErrorRef.current = currentError;
      return;
    }

    if (currentData && dataChanged) {
      isProcessingRef.current = true;
      
      try {
        setIsSubmitting(false);
        
        if (currentData.data && currentData.data.data) {
          const apiData = currentData.data.data;
          
          setShowToast({
            type: 'success',
            message: `Download initiated successfully for ${getEquipmentList().length} equipment items!`,
            show: true
          });

          setTimeout(() => {
            setShowToast(prev => prev.show ? { ...prev, show: false } : prev);
          }, 5000);

          if (apiData.download_id) {
            const saveSuccess = saveDownloadId(apiData.download_id);
            
            if (!saveSuccess) {
              console.warn('Failed to save download ID to localStorage');
            }

            const downloadIds = getDownloadIds();

            if (apiData.download_status === 0) {
              console.log('Combined download initiated with ID:', apiData.download_id);
              dispatch(
                setExcelDownload({
                  ...set_excel_download,
                  download_ids: downloadIds,
                  download_status: true
                })
              );
            } else {
              dispatch(
                setExcelDownload({
                  ...set_excel_download,
                  cache_download_id: apiData.download_id,
                  download_status: true
                })
              );
            }
          }
        } else {
          console.error('Unexpected API response format:', currentData);
          setShowToast({
            type: 'error',
            message: 'Unexpected response from server',
            show: true
          });
        }
      } finally {
        previousDataRef.current = currentData;
        setTimeout(() => {
          isProcessingRef.current = false;
        }, 100);
      }
    }
  }, [data, error, dispatch, set_excel_download, setShowToast, saveDownloadId, getDownloadIds, getEquipmentList]);

  useEffect(() => {
    return () => {
      setIsSubmitting(false);
      isProcessingRef.current = false;
    };
  }, []);

  return (
    <SetDownloadComponent
      onFormSubmit={onFormSubmit}
      formData={formData}
      onSelectOption={onSelectOption}
      setFormData={setFormData}
      openDownloadScreen={openDownloadScreen}
      excelDonwload={set_excel_download}
      selectSensorClassification={selectSensorClassification}
      sensorUnit={sensorUnit}
      sensorClassificationSlug={sensorClassificationSlug}
      currentSingleDataTypeSensorValueInfo={currentSingleDataTypeSensorValueInfo}
      handleSingleDataTypeSensorValue={handleSingleDataTypeSensorValue}
      isSubmitting={isSubmitting || loading}
      equipmentCount={getEquipmentList().length}
      debugCheckedItems={debugCheckedItems} // Optional: pass debug function to component
    />
  );
}